/**
 * MAVIINCI Interview Companion — greeting cards ka PDF.
 * Onyx & Gold design (front = customer naam + rotating quote, back = brand + QR).
 * Sticker jaisa hi: button dabao → PDF seedha download (doc.save).
 *
 * Design image-heavy hai (gold logos, QR, gradient, serif fonts), isliye har
 * card ko asli DOM me banake html2canvas se capture karte hain — jo dikhta hai
 * wahi PDF me. 4 card per A4 (2×2). Fronts ke baad backs (column-mirrored) taaki
 * duplex "flip on long edge" print me back sahi front ke peeche aaye aur
 * hole-punch dot aar-paar align rahe.
 */
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import INTERVIEW_QUOTES from "./campus_card_quotes";
import { EMBLEM_SVG, WORDMARK_SVG, QR_PNG } from "./campus_card_assets";

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

// ── Card + sheet geometry (inches) ──
// Sheet 12×18 (sticker wala hi kagaz), landscape = 18 chaura × 12 uncha.
// 5 column × 2 row = 10 card per sheet (cards ek-doosre se sate hue, ek hi
// cut line se kat jaayein).
const CARD_W_IN = 3.5;
const CARD_H_IN = 5;
const SHEET_W_IN = 18;
const SHEET_H_IN = 12;
const COLS = 5;
const ROWS = 2;
const PER_PAGE = COLS * ROWS;
const MARGIN_X = (SHEET_W_IN - COLS * CARD_W_IN) / 2;  // 0.25"
const MARGIN_Y = (SHEET_H_IN - ROWS * CARD_H_IN) / 2;  // 1.0"

// DOM render size (px). High-ish for crisp print (~300dpi over 3.5in ≈ 1050px).
const NODE_W = 360;
const NODE_H = 515;
const RENDER_SCALE = 3;

const titleCase = (s) =>
    String(s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) || "—";

/** Invoice ke number se stable quote index (dobara wahi card = wahi quote). */
const quoteForInvoice = (invoiceNo) => {
    const s = String(invoiceNo || "");
    const m = s.match(/(\d+)\s*$/);
    let idx;
    if (m) {
        idx = (parseInt(m[1], 10) - 1) % INTERVIEW_QUOTES.length;
    } else {
        let h = 0;
        for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
        idx = h % INTERVIEW_QUOTES.length;
    }
    if (idx < 0) idx += INTERVIEW_QUOTES.length;
    return INTERVIEW_QUOTES[idx] || [];
};

/** Quote lines ki ginti ke hisaab se font (lambi quote chhoti). */
const quoteFontPx = (n) => (n >= 6 ? 17 : n === 5 ? 19 : n <= 2 ? 25 : 22);

const CARD_CSS = `
.mvc-stage{position:fixed;left:-99999px;top:0;z-index:-1}
.mvc-card{width:${NODE_W}px;height:${NODE_H}px;position:relative;overflow:hidden;
  display:flex;flex-direction:column;box-sizing:border-box;
  background:#0e0e13;background:radial-gradient(125% 90% at 50% 0%,#20212a 0%,#0e0e13 58%,#08080b 100%);
  color:#efe7d4;font-family:'Inter',system-ui,sans-serif}
.mvc-card *{box-sizing:border-box;margin:0;padding:0}
.mvc-frame{position:absolute;inset:14px;border:1px solid rgba(201,164,92,.42);border-radius:7px;pointer-events:none}
.mvc-frame::before,.mvc-frame::after{content:'';position:absolute;width:9px;height:9px;border:1px solid #c9a45c}
.mvc-frame::before{top:-1px;left:-1px;border-right:0;border-bottom:0}
.mvc-frame::after{bottom:-1px;right:-1px;border-left:0;border-top:0}
/* hole-punch dot (nested divs — html2canvas box-shadow skip karta hai) */
.mvc-hole{position:absolute;top:26px;width:22px;height:22px;border-radius:50%;
  background:radial-gradient(circle at 38% 34%,#e3c67e,#c9a45c 60%,#a9863f 100%)}
.mvc-hole i{position:absolute;inset:6px;border-radius:50%;background:#0a0a0c;opacity:.4}
.mvc-emb svg,.mvc-logo svg{display:block}
.mvc-gold{color:#c9a45c}
.mvc-kick{font-size:8px;letter-spacing:.38em;text-transform:uppercase;color:#c9a45c}
.mvc-q{font-family:'Cormorant Garamond','Playfair Display',serif;font-weight:500;color:#f4ecda}
.mvc-wish{font-family:'Cormorant','Cormorant Garamond',serif;font-style:italic;color:#c9b787}
.mvc-divg{display:flex;align-items:center;gap:9px;justify-content:center}
.mvc-divg .l{height:1px;width:42px;background:linear-gradient(90deg,rgba(201,164,92,0),#c9a45c)}
.mvc-divg .r{height:1px;width:42px;background:linear-gradient(90deg,#c9a45c,rgba(201,164,92,0))}
.mvc-divg .dot{width:4px;height:4px;background:#c9a45c;transform:rotate(45deg)}

/* FRONT */
.mvc-f{padding:30px 26px;align-items:center;text-align:center}
.mvc-f .mvc-hole{right:26px}
.mvc-f .mvc-emb{color:#c9a45c;width:40px;height:34px;margin-top:22px}
.mvc-f .mvc-logo{color:#c9a45c;width:100px;height:15px;margin-top:11px}
.mvc-f .mvc-kick{margin-top:7px}
.mvc-forln{margin:20px 0 3px;font-family:'Cormorant','Cormorant Garamond',serif;font-style:italic;font-size:13px;color:#b3a077;letter-spacing:.04em}
.mvc-name{font-family:'Playfair Display',serif;font-size:27px;color:#e7c988;font-weight:600;letter-spacing:.01em;line-height:1.15}
.mvc-f .mvc-q{line-height:1.46;margin:auto 0}
.mvc-lead{font-family:'Playfair Display',serif;font-size:34px;color:#c9a45c;line-height:0;display:block;height:12px}
.mvc-f .mvc-divg{margin:13px 0 8px}
.mvc-f .mvc-wish{font-size:14px}
.mvc-inv{position:absolute;bottom:22px;right:24px;font-size:10.5px;font-weight:600;letter-spacing:.16em;color:rgba(201,164,92,.92)}

/* BACK (sabka same) */
.mvc-b{padding:34px 26px 28px;align-items:center;text-align:center;justify-content:space-between}
.mvc-b .mvc-hole{left:26px}
.mvc-b .mvc-emb{color:#c9a45c;width:78px;height:66px;margin-top:14px}
.mvc-b .mvc-logo{color:#c9a45c;width:200px;height:30px;margin-top:14px}
.mvc-tl{font-family:'Cormorant','Cormorant Garamond',serif;font-style:italic;font-size:14px;color:#cdbf9f;margin-top:9px}
.mvc-qtile{width:120px;height:120px;background:#f4ecda;border-radius:9px;padding:11px;margin:2px 0}
.mvc-qtile img{width:100%;height:100%;display:block}
.mvc-scan{font-size:8.5px;letter-spacing:.24em;text-transform:uppercase;color:#c9a45c}
.mvc-links{font-size:12px;color:#e4dcc9;line-height:1.9;letter-spacing:.02em}
.mvc-links b{color:#f4ecda;font-weight:600}
.mvc-fine{font-size:8px;letter-spacing:.14em;text-transform:uppercase;color:rgba(228,220,201,.4)}
`;

let stageEl = null;
const ensureStage = () => {
    if (stageEl && document.body.contains(stageEl)) return stageEl;
    // fonts (Cormorant app me nahi hai) + design fonts
    if (!document.getElementById("mvc-fonts")) {
        const link = document.createElement("link");
        link.id = "mvc-fonts";
        link.rel = "stylesheet";
        link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,500&family=Cormorant:ital,wght@0,500;1,400;1,500&family=Playfair+Display:ital,wght@0,500;0,600;0,700&display=swap";
        document.head.appendChild(link);
    }
    if (!document.getElementById("mvc-css")) {
        const st = document.createElement("style");
        st.id = "mvc-css";
        st.textContent = CARD_CSS;
        document.head.appendChild(st);
    }
    stageEl = document.createElement("div");
    stageEl.className = "mvc-stage";
    document.body.appendChild(stageEl);
    return stageEl;
};

const frontHTML = (booking) => {
    const name = titleCase(booking.customer_name);
    const inv = String(booking.invoice_no || "").trim();
    const lines = quoteForInvoice(inv);
    const fs = quoteFontPx(lines.length);
    return `
    <div class="mvc-card mvc-f">
      <div class="mvc-frame"></div>
      <div class="mvc-hole"><i></i></div>
      <span class="mvc-emb">${EMBLEM_SVG}</span>
      <span class="mvc-logo">${WORDMARK_SVG}</span>
      <div class="mvc-kick">Interview Companion</div>
      <div class="mvc-forln">Prepared for</div>
      <div class="mvc-name">${esc(name)}</div>
      <div class="mvc-q" style="font-size:${fs}px"><span class="mvc-lead">&ldquo;</span>${lines.map(esc).join("<br/>")}</div>
      <div class="mvc-divg"><span class="l"></span><span class="dot"></span><span class="r"></span></div>
      <div class="mvc-wish">Best of luck for your interview</div>
      ${inv ? `<div class="mvc-inv">${esc(inv)}</div>` : ""}
    </div>`;
};

const backHTML = () => `
    <div class="mvc-card mvc-b">
      <div class="mvc-frame"></div>
      <div class="mvc-hole"><i></i></div>
      <span class="mvc-emb">${EMBLEM_SVG}</span>
      <span class="mvc-logo">${WORDMARK_SVG}</span>
      <div class="mvc-tl">Bespoke tailoring, crafted to your measure</div>
      <div class="mvc-qtile"><img src="${QR_PNG}" alt="QR"/></div>
      <div class="mvc-scan">Scan · maviinci.in</div>
      <div class="mvc-links"><b>www.maviinci.in</b><br/>Instagram maviinci_in<br/>connect@bluepearlindustries.com</div>
      <div class="mvc-fine">Blue Pearl Industries · Bhilai · Chhattisgarh</div>
    </div>`;

const renderToImage = async (stage, html) => {
    stage.innerHTML = html;
    const node = stage.firstElementChild;
    const canvas = await html2canvas(node, {
        scale: RENDER_SCALE,
        backgroundColor: null,
        useCORS: true,
        logging: false,
        width: NODE_W,
        height: NODE_H,
    });
    return canvas.toDataURL("image/jpeg", 0.92);
};

const placeCard = (doc, img, col, row) => {
    const x = MARGIN_X + col * CARD_W_IN;
    const y = MARGIN_Y + row * CARD_H_IN;
    doc.addImage(img, "JPEG", x, y, CARD_W_IN, CARD_H_IN);
    // halka cut-guide corner ticks
    doc.setDrawColor(150);
    doc.setLineWidth(0.004);
    const t = 0.12;
    [[x, y, 1, 1], [x + CARD_W_IN, y, -1, 1], [x, y + CARD_H_IN, 1, -1], [x + CARD_W_IN, y + CARD_H_IN, -1, -1]]
        .forEach(([cx, cy, sx, sy]) => {
            doc.line(cx, cy, cx + sx * t, cy);
            doc.line(cx, cy, cx, cy + sy * t);
        });
};

/**
 * @param {Array} bookings  already drive/campus filtered
 * @param {(msg:string)=>void} [onProgress]
 */
export async function generateInterviewCardsPDF(bookings, onProgress) {
    const list = [...(bookings || [])]
        .filter((b) => String(b.invoice_no || "").trim() && String(b.customer_name || "").trim())
        .sort((a, b) => String(a.invoice_no || "").localeCompare(String(b.invoice_no || ""), undefined, { numeric: true }));

    if (!list.length) {
        alert("No orders with invoice + name found for cards.");
        return;
    }

    onProgress && onProgress("Preparing…");
    const stage = ensureStage();

    // Fonts best-effort: agar Google Fonts slow/blocked ho to `fonts.ready`
    // kabhi resolve nahi hota (exception bhi nahi) — isliye timeout ke saath
    // race karo, warna poora PDF wahin chup-chaap atak jaata hai.
    const withTimeout = (p, ms) => Promise.race([p, new Promise((r) => setTimeout(r, ms))]);
    try {
        if (document.fonts) {
            await withTimeout(Promise.all([
                document.fonts.ready,
                document.fonts.load("500 22px 'Cormorant Garamond'"),
                document.fonts.load("italic 14px 'Cormorant'"),
                document.fonts.load("600 27px 'Playfair Display'"),
            ]).catch(() => {}), 6000);
            await new Promise((r) => setTimeout(r, 150));
        }
    } catch (e) { /* fonts best-effort */ }

    const doc = new jsPDF({
        unit: "in",
        format: [SHEET_H_IN, SHEET_W_IN], // [12,18] + landscape => 18×12
        orientation: "landscape",
        compress: true,
    });

    // back sabka same — ek hi baar render karke reuse
    const backImg = await renderToImage(stage, backHTML());

    const pages = [];
    for (let i = 0; i < list.length; i += PER_PAGE) pages.push(list.slice(i, i + PER_PAGE));

    // Pehle SAARE front pages, uske baad SAARE back pages (usi order me) —
    // taaki: pehle front wale pages print karo, sheet ko left-right palat kar
    // dobara daalo, phir back wale pages print karo.
    // Back pe column ulta (COLS-1-col) hota hai, kyunki palatne par left-right
    // badal jaata hai — isse har back exactly apne front ke peeche baithta hai
    // aur hole-punch dot bhi aar-paar mil jaata hai.
    let done = 0;
    for (let p = 0; p < pages.length; p++) {
        if (p > 0) doc.addPage([SHEET_H_IN, SHEET_W_IN], "landscape");
        const chunk = pages[p];
        for (let i = 0; i < chunk.length; i++) {
            const img = await renderToImage(stage, frontHTML(chunk[i]));
            placeCard(doc, img, i % COLS, Math.floor(i / COLS));
            done += 1;
            onProgress && onProgress(`Rendering ${done}/${list.length}…`);
        }
    }
    for (let p = 0; p < pages.length; p++) {
        doc.addPage([SHEET_H_IN, SHEET_W_IN], "landscape");
        const chunk = pages[p];
        for (let i = 0; i < chunk.length; i++) {
            placeCard(doc, backImg, COLS - 1 - (i % COLS), Math.floor(i / COLS));
        }
    }

    stage.innerHTML = "";
    onProgress && onProgress("");
    doc.save(`interview-cards-${list.length}.pdf`);
}

export default generateInterviewCardsPDF;
